import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Heading, Text, TextField, TextArea } from "@radix-ui/themes"
import { LoaderCircle } from "lucide-react"
import type { FormEventHandler } from "react"
import { useState } from "react"

import InputError from "@/components/input-error"
import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { projectEventsPath, projectDashboardPath } from "@/routes"

interface EventForm {
  email: string
  name: string
  custom_fields: string
}

interface NewEventProps {
  project: {
    id: number
    name: string
    slug: string
  }
}

export default function NewEvent({ project }: NewEventProps) {
  const { data, setData, post, processing, errors } = useForm<Required<EventForm>>({
    email: "",
    name: "",
    custom_fields: "",
  })

  const [customFieldsError, setCustomFieldsError] = useState<string | null>(null)

  // Validate JSON in real-time
  const validateCustomFields = (value: string) => {
    if (value.trim() === "") {
      setCustomFieldsError(null)
      return true
    }

    try {
      JSON.parse(value)
      setCustomFieldsError(null)
      return true
    } catch (error) {
      setCustomFieldsError("Invalid JSON format")
      return false
    }
  }

  // Check if form is valid for submission
  const isFormValid =
    data.email.trim() !== "" &&
    data.name.trim() !== "" &&
    (data.custom_fields.trim() === "" || customFieldsError === null)

  const submit: FormEventHandler = (e) => {
    e.preventDefault()

    if (!isFormValid) {
      return
    }

    post(projectEventsPath(project.slug))
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Create Testing Event" },
      ]}
    >
      <Head title="New Testing Event" />
      <Flex direction="column" gap="6" p="6">
        <Heading size="6" weight="medium">
          Create Testing Event
        </Heading>

        <form className="flex flex-col gap-6" onSubmit={submit}>
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Flex justify="between">
                <Text as="label" htmlFor="email" size="2" weight="medium">
                  Customer Email
                </Text>

                <InputError message={errors.email} />
              </Flex>

              <TextField.Root
                id="email"
                type="email"
                autoFocus
                tabIndex={1}
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                placeholder="customer@example.com"
              />
            </div>

            <div className="grid gap-2">
              <Flex justify="between">
                <Text as="label" htmlFor="name" size="2" weight="medium">
                  Event Name
                </Text>

                <InputError message={errors.name} />
              </Flex>

              <TextField.Root
                id="name"
                type="text"
                tabIndex={2}
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder="Page viewed, Button clicked, etc."
              />
            </div>

            <div className="grid gap-2">
              <Flex justify="between">
                <Text as="label" htmlFor="custom_fields" size="2" weight="medium">
                  Custom Fields (JSON)
                </Text>

                <InputError message={customFieldsError || errors.custom_fields} />
              </Flex>

              <TextArea
                id="custom_fields"
                tabIndex={3}
                value={data.custom_fields}
                onChange={(e) => {
                  const value = e.target.value
                  setData("custom_fields", value)
                  validateCustomFields(value)
                }}
                placeholder='{"key": "value", "count": 123}'
                rows={4}
              />
            </div>

            <Flex gap="3" mt="4">
              <Button
                type="submit"
                className="w-full"
                tabIndex={4}
                disabled={processing || !isFormValid}
              >
                {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                Create Event
              </Button>

              <LinkButton
                href={projectDashboardPath(project.slug)}
                variant="soft"
                className="w-full"
                tabIndex={5}
              >
                Cancel
              </LinkButton>
            </Flex>
          </div>
        </form>
      </Flex>
    </ProjectLayout>
  )
}
